import { resetRecordFormData } from "components/utils/modal-helper";
import { SBSensor } from "features/iot/sensor-batch/models/sensor-batch-models";
import { useState, useCallback, useEffect } from "react";

export const mapSensorToFormData = (sensor?: SBSensor) => ({
  serialNumber: sensor?.serialNumber || "",
  macAddress: sensor?.macAddress || "",
  pitch: String(sensor?.pitch || 0),
  numberOfResistiveElements: String(sensor?.numberOfResistiveElements || 0),
  hardwareRevision: sensor?.hardwareRevision || "",
  firmwareRevision: sensor?.firmwareRevision || "",
  offset: String(sensor?.offset || 0),
});

// Validation function to validate form fields
const validateForm = (formData: Record<string, string>) => {
  const errors: Record<string, string> = {};

  if (!formData.serialNumber.trim()) {
    errors.serialNumber = "Serial number is required";
  }

  if (!formData.macAddress.trim()) {
    errors.macAddress = "MAC address is required";
  } else if (!/^[0-9A-Fa-f]{16}$/.test(formData.macAddress)) {
    errors.macAddress = "Invalid MAC address (must be 16 hex characters)";
  }

  if (!formData.pitch.trim()) {
    errors.pitch = "Pitch is required";
  }

  if (!formData.numberOfResistiveElements.trim()) {
    errors.numberOfResistiveElements =
      "Number of resistive elements is required";
  } else if (Number(formData.numberOfResistiveElements) <= 0) {
    errors.numberOfResistiveElements = "Must be a positive number";
  }

  if (!formData.hardwareRevision.trim()) {
    errors.hardwareRevision = "Hardware revision is required";
  }

  if (!formData.firmwareRevision.trim()) {
    errors.firmwareRevision = "Firmware revision is required";
  }

  if (!formData.offset.trim()) {
    errors.offset = "Offset is required";
  }
  return errors;
};

export const useSensorForm = (sensor?: SBSensor) => {
  const [formData, setFormData] = useState<Record<string, string>>({
    serialNumber: sensor?.serialNumber || "",
    macAddress: sensor?.macAddress || "",
    pitch: String(sensor?.pitch || 0),
    numberOfResistiveElements: String(sensor?.numberOfResistiveElements || 0),
    hardwareRevision: sensor?.hardwareRevision || "",
    firmwareRevision: sensor?.firmwareRevision || "",
    offset: String(sensor?.offset || 0)
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isEditing, setIsEditing] = useState(Boolean(sensor?.macAddress));

  const resetForm = () => {
    setFormData({
      serialNumber: "",
      macAddress: "",
      offset: "",
      pitch: "",
      numberOfResistiveElements: "0",
      hardwareRevision: "",
      firmwareRevision: "",
      uploadCSVName: "",
    });
    setFormErrors({});
    setIsEditing(false);
  };

  // Initialize form data based on sensor
  useEffect(() => {
    if (sensor) {
      setIsEditing(Boolean(sensor.macAddress));
      setFormData(mapSensorToFormData(sensor));
    } else {
      resetRecordFormData(setFormData);
    }
  }, [sensor]);

  // Handle form field changes
  const handleChange = useCallback((fieldName: string, value: string) => {
    setFormData((prevData) => ({
      ...prevData,
      [fieldName]: value,
    }));
  }, []);

  // Handle form field validation
  const handleValidation = (
    fieldName: string,
    validationError: string | any
  ): void => {
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [fieldName]: validationError,
    }));
  };

  // Validate the entire form before submission
  const validateFormFields = () => {
    const errors = validateForm(formData);
    setFormErrors(errors);
    const hasErrors = Object.values(errors).some((error) => error !== "");
    return !hasErrors; // Return true if no errors
  };

  const clearUploadCSVName = () => {
    setFormData((prevData) => ({
      ...prevData,
      uploadCSVName: "", // Clear the uploadCSVName field
    }));
  };

  return {
    setFormData,
    setFormErrors,
    formData,
    formErrors,
    handleChange,
    handleValidation,
    isEditing,
    setIsEditing,
    resetForm,
    validateFormFields,
    clearUploadCSVName,
  };
};
